/**
 * Enhanced error handling for the HRx Investigation Consent Service
 * 
 * This file provides custom error classes and error handling utilities
 * to improve error reporting, logging, and user experience.
 */

import { HTTP_STATUS, VALIDATION_MESSAGES, RETRY_CONFIG } from '../config/consentConfig';

// Base API Error class
export class ApiError extends Error {
  public readonly statusCode?: number;
  public readonly code?: string;
  public readonly context?: string;
  public readonly timestamp: number;

  constructor(
    message: string,
    statusCode?: number,
    code?: string,
    context?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.context = context;
    this.timestamp = Date.now();

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  // Convert to user-friendly message
  toUserMessage(): string {
    switch (this.statusCode) {
      case HTTP_STATUS.UNAUTHORIZED:
        return VALIDATION_MESSAGES.UNAUTHORIZED_ACCESS;
      case HTTP_STATUS.FORBIDDEN:
        return VALIDATION_MESSAGES.UNAUTHORIZED_ACCESS;
      case HTTP_STATUS.NOT_FOUND:
        return 'The requested resource was not found.';
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        return 'A server error occurred. Please try again later.';
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        return 'The service is temporarily unavailable. Please try again later.';
      default:
        return this.message || VALIDATION_MESSAGES.NETWORK_ERROR;
    }
  }

  // Convert to JSON for logging
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      code: this.code,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
}

// Validation Error class
export class ValidationError extends Error {
  public readonly field?: string;
  public readonly value?: any;

  constructor(message: string, field?: string, value?: any) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.value = value;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }
}

// Token Error class
export class TokenError extends Error {
  public readonly tokenType: 'invalid' | 'expired' | 'missing';

  constructor(message: string, tokenType: 'invalid' | 'expired' | 'missing') {
    super(message);
    this.name = 'TokenError';
    this.tokenType = tokenType;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TokenError);
    }
  }
}

// Network Error class
export class NetworkError extends ApiError {
  constructor(message: string = VALIDATION_MESSAGES.NETWORK_ERROR, context?: string) {
    super(message, undefined, 'NETWORK_ERROR', context);
    this.name = 'NetworkError';
  }
}

// Service Error class
export class ServiceError extends ApiError {
  constructor(message: string, context?: string) {
    super(message, undefined, 'SERVICE_ERROR', context);
    this.name = 'ServiceError';
  }
}

// Error handling utility class
export class ConsentErrorHandler {
  /**
   * Handle API errors with proper logging and user-friendly messages
   */
  static handleApiError(error: unknown, context: string): {
    success: false;
    error: string;
    code?: string;
  } {
    // Log the error for debugging
    ConsentErrorHandler.logError(error, context);

    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.toUserMessage(),
        code: error.code
      };
    }

    if (error instanceof ValidationError) {
      return {
        success: false,
        error: error.message,
        code: 'VALIDATION_ERROR'
      };
    }

    if (error instanceof TokenError) {
      return {
        success: false,
        error: ConsentErrorHandler.getTokenErrorMessage(error.tokenType),
        code: 'TOKEN_ERROR'
      };
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        error: VALIDATION_MESSAGES.NETWORK_ERROR,
        code: 'NETWORK_ERROR'
      };
    }

    // Generic error handling
    return {
      success: false,
      error: VALIDATION_MESSAGES.SUBMISSION_ERROR,
      code: 'UNKNOWN_ERROR'
    };
  }

  /**
   * Retry mechanism with exponential backoff
   */
  static async withRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = RETRY_CONFIG.MAX_RETRIES,
    initialDelay: number = RETRY_CONFIG.INITIAL_DELAY
  ): Promise<T> {
    let lastError: Error;
    let delay = initialDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Don't retry on validation errors or client errors
        if (error instanceof ValidationError || 
            error instanceof TokenError ||
            (error instanceof ApiError && error.statusCode && error.statusCode < 500)) {
          throw error;
        }

        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          break;
        }

        // Wait before retrying
        await ConsentErrorHandler.delay(delay);
        
        // Exponential backoff with jitter
        delay = Math.min(
          delay * RETRY_CONFIG.BACKOFF_MULTIPLIER + Math.random() * 1000,
          RETRY_CONFIG.MAX_DELAY
        );

        console.warn(`Retry attempt ${attempt + 1}/${maxRetries} for operation after ${delay}ms delay`);
      }
    }

    throw lastError!;
  }

  /**
   * Validate consent form data
   */
  static validateConsentSubmission(
    consentDecision: string | null,
    consentText?: string,
    requiredText?: string
  ): void {
    if (!consentDecision) {
      throw new ValidationError(VALIDATION_MESSAGES.NO_SELECTION, 'consentDecision');
    }

    if (consentDecision === 'accept' && requiredText && consentText?.trim()?.toLowerCase() !== requiredText.toLowerCase()) {
      throw new ValidationError(
        VALIDATION_MESSAGES.INVALID_CONSENT_TEXT(requiredText),
        'consentText',
        consentText
      );
    }
  }

  /**
   * Create API error from fetch response
   */
  static async createApiErrorFromResponse(response: Response, context: string): Promise<ApiError> {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode = `HTTP_${response.status}`;

    try {
      // Try to parse error response
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('application/cbor')) {
        // Handle CBOR error response
        const CBOR = require('cbor-js');
        const errorBuffer = await response.arrayBuffer();
        const errorData = CBOR.decode(errorBuffer);
        errorMessage = errorData.message || errorMessage;
        errorCode = errorData.code || errorCode;
      } else if (contentType?.includes('application/json')) {
        // Handle JSON error response
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        errorCode = errorData.code || errorCode;
      } else {
        // Handle text error response
        const textError = await response.text();
        if (textError) {
          errorMessage = textError;
        }
      }
    } catch (parseError) {
      // If we can't parse the error response, use the default message
      console.warn('Failed to parse error response:', parseError);
    }

    return new ApiError(errorMessage, response.status, errorCode, context);
  }

  /**
   * Log errors with proper context
   */
  static logError(error: unknown, context: string): void {
    const errorInfo = {
      context,
      timestamp: new Date().toISOString(),
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...(error instanceof ApiError ? error.toJSON() : {})
      } : String(error)
    };

    console.error(`[ConsentErrorHandler] ${context}:`, errorInfo);
  }

  /**
   * Get user-friendly message for token errors
   */
  static getTokenErrorMessage(tokenType: 'invalid' | 'expired' | 'missing'): string {
    switch (tokenType) {
      case 'missing':
        return VALIDATION_MESSAGES.TOKEN_NOT_PROVIDED;
      case 'expired':
      case 'invalid':
        return VALIDATION_MESSAGES.TOKEN_INVALID_OR_EXPIRED;
      default:
        return VALIDATION_MESSAGES.TOKEN_VALIDATION_ERROR;
    }
  }

  /**
   * Utility function to create a delay
   */
  static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export utility functions
export const handleApiError = ConsentErrorHandler.handleApiError;
export const withRetry = ConsentErrorHandler.withRetry;
export const validateConsentSubmission = ConsentErrorHandler.validateConsentSubmission;

export default ConsentErrorHandler;
